/*!
 * Mast free shipping offer — eligibility check via Shopify Cart Shipping Rates API.
 * Verified rule: orders ≥ €150 to European addresses get free shipping.
 */
(function () {
  "use strict";

  var THRESHOLD_CENTS = 15000;
  var EUR_CURRENCY = "EUR";

  var EUROPE_COUNTRIES = {
    AT: 1,
    BE: 1,
    BG: 1,
    HR: 1,
    CY: 1,
    CZ: 1,
    DK: 1,
    EE: 1,
    FI: 1,
    FR: 1,
    DE: 1,
    GR: 1,
    HU: 1,
    IE: 1,
    IT: 1,
    LV: 1,
    LT: 1,
    LU: 1,
    MT: 1,
    NL: 1,
    PL: 1,
    PT: 1,
    RO: 1,
    SK: 1,
    SI: 1,
    ES: 1,
    SE: 1,
    GB: 1,
    CH: 1,
    NO: 1,
    IS: 1,
    LI: 1,
    MC: 1,
    AD: 1,
    SM: 1,
    VA: 1,
    AL: 1,
    BA: 1,
    ME: 1,
    MK: 1,
    RS: 1,
    XK: 1,
    MD: 1,
    UA: 1,
    BY: 1,
    GE: 1,
    AM: 1,
    AZ: 1,
    TR: 1,
  };

  var DEFAULT_ZIP = {
    FR: "75001",
    DE: "10115",
    GB: "SW1A1AA",
    IT: "00118",
    ES: "28001",
    NL: "1012AB",
    BE: "1000",
    AT: "1010",
    PT: "1000-001",
    PL: "00-001",
    SE: "11120",
    DK: "1050",
    FI: "00100",
    IE: "D01 F5P2",
    CH: "8001",
  };

  function isEurope(countryCode) {
    return !!(countryCode && EUROPE_COUNTRIES[countryCode.toUpperCase()]);
  }

  function isEligibleMarket(countryCode, currencyCode) {
    return isEurope(countryCode) && String(currencyCode || "").toUpperCase() === EUR_CURRENCY;
  }

  function meetsThreshold(priceCents) {
    return priceCents >= THRESHOLD_CENTS;
  }

  function parsePriceCents(price) {
    if (typeof price === "number") return price;
    if (typeof price === "string") {
      var parsed = parseFloat(price.replace(/[^\d.]/g, ""));
      return isNaN(parsed) ? 0 : Math.round(parsed * 100);
    }
    return 0;
  }

  function showOffer(el) {
    if (!el) return;
    el.hidden = false;
    el.classList.add("is-visible");
  }

  function hideOffer(el) {
    if (!el) return;
    el.hidden = true;
    el.classList.remove("is-visible");
  }

  function updateOffer(el, priceCents) {
    if (!el) return;
    var country = el.dataset.country || "";
    var currency = el.dataset.currency || "";
    if (isEligibleMarket(country, currency) && meetsThreshold(priceCents)) {
      showOffer(el);
    } else {
      hideOffer(el);
    }
  }

  /**
   * Verify free shipping via Shopify Cart Shipping Rates API.
   * Saves and restores the customer's cart to avoid side effects.
   */
  function verifyFreeShippingViaApi(variantId, countryCode) {
    var cacheKey = "mastFreeShip:" + variantId + ":" + countryCode;
    try {
      var cached = sessionStorage.getItem(cacheKey);
      if (cached !== null) return Promise.resolve(cached === "true");
    } catch (e) {
      /* ignore */
    }

    var zip = DEFAULT_ZIP[countryCode] || "75001";
    var params = new URLSearchParams({
      "shipping_address[zip]": zip,
      "shipping_address[country]": countryCode,
      "shipping_address[province]": "",
    });

    var savedCart = null;

    return fetch(Shopify.routes.root + "cart.js")
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        savedCart = cart;
        return fetch(Shopify.routes.root + "cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        });
      })
      .then(function () {
        return fetch(Shopify.routes.root + "cart/prepare_shipping_rates.json?" + params.toString(), { method: "POST" });
      })
      .then(function () {
        return pollShippingRates(params);
      })
      .then(function (rates) {
        var hasFree = rates.some(function (r) {
          return parseFloat(r.price) === 0;
        });
        try {
          sessionStorage.setItem(cacheKey, hasFree ? "true" : "false");
        } catch (e) {
          /* ignore */
        }
        return hasFree;
      })
      .catch(function () {
        return false;
      })
      .then(function (result) {
        return restoreCart(savedCart).then(function () {
          return result;
        });
      });
  }

  function pollShippingRates(params, attempt) {
    attempt = attempt || 0;
    if (attempt > 15) return Promise.resolve([]);

    return fetch(Shopify.routes.root + "cart/async_shipping_rates.json?" + params.toString())
      .then(function (r) {
        return r.text();
      })
      .then(function (text) {
        if (text.trim() === "null") {
          return new Promise(function (resolve) {
            setTimeout(function () {
              resolve(pollShippingRates(params, attempt + 1));
            }, 500);
          });
        }
        try {
          return JSON.parse(text).shipping_rates || [];
        } catch (e) {
          return [];
        }
      });
  }

  function restoreCart(savedCart) {
    if (!savedCart) return Promise.resolve();

    return fetch(Shopify.routes.root + "cart/clear.js", { method: "POST" })
      .then(function () {
        if (!savedCart.items || savedCart.items.length === 0) return;
        var items = savedCart.items.map(function (item) {
          return { id: item.variant_id, quantity: item.quantity, properties: item.properties };
        });
        return fetch(Shopify.routes.root + "cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items: items }),
        });
      })
      .catch(function () {
        /* best effort */
      });
  }

  function initOffer(el, options) {
    if (!el) return;
    options = options || {};

    var priceCents = parsePriceCents(options.price);
    updateOffer(el, priceCents);

    if (options.variantId && options.verifyApi && isEligibleMarket(el.dataset.country, el.dataset.currency)) {
      verifyFreeShippingViaApi(options.variantId, el.dataset.country).then(function (confirmed) {
        if (confirmed && isEligibleMarket(el.dataset.country, el.dataset.currency) && meetsThreshold(priceCents)) {
          showOffer(el);
        } else if (!confirmed) {
          hideOffer(el);
        }
      });
    }
  }

  window.MastFreeShipping = {
    THRESHOLD_CENTS: THRESHOLD_CENTS,
    isEurope: isEurope,
    isEligibleMarket: isEligibleMarket,
    meetsThreshold: meetsThreshold,
    parsePriceCents: parsePriceCents,
    updateOffer: updateOffer,
    initOffer: initOffer,
    verifyFreeShippingViaApi: verifyFreeShippingViaApi,
  };
})();
